import { DeductionListJson } from "@/api"
import { filter_won_pending_payment_rows } from "@/deduction/won_pending_payment"
import { Button } from "@/components/ui/button"
import { AlertTriangle } from "lucide-react"
import { exactCurrencyFormatter, to_reporting_amount } from "@/utils"
import { useLocation } from "wouter-preact"

export interface DeductionAlertData {
  key: string
  title: string
  count: number
  amount: number
  href: string
}

// Unmatched repayments are worked in the admin panel (/unmatched-repayments), where ops can match
// them to a deduction or record why they never can be, so they are deliberately not surfaced here.
export function computeDeductionAlerts(data: DeductionListJson[]): DeductionAlertData[] {
  const alerts: DeductionAlertData[] = []

  // Won deductions without a repayment (matches Won — Pending Payment filtered page)
  const wonWithoutRepay = filter_won_pending_payment_rows(data)
  if (wonWithoutRepay.length > 0) {
    const totalCents = wonWithoutRepay.reduce((sum, row) => {
      const amountCents = Math.round(Math.abs(to_reporting_amount(row, row.won_amount ?? row.deduction_amount ?? 0)) * 100)
      return sum + amountCents
    }, 0)
    alerts.push({
      key: "won-pending-payment",
      title: "Won — Pending Payment",
      count: wonWithoutRepay.length,
      amount: totalCents / 100,
      href: "/deductions/won-pending-payment",
    })
  }

  return alerts
}

interface DeductionAlertsProps {
  alerts: DeductionAlertData[]
}

export function DeductionAlerts({ alerts }: DeductionAlertsProps) {
  const [, navigate] = useLocation()

  if (alerts.length === 0) return null

  return (
    <div class="mt-6 flex flex-col gap-3 mb-4">
      {alerts.map(alert => (
        <div key={alert.key} class="rounded-md border border-amber-300 bg-amber-50 px-4 py-3.5">
          <div class="flex items-start justify-between gap-3">
            <div class="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-700 shrink-0" />
              <div>
                <div class="text-sm font-medium text-amber-900">{alert.title}</div>
                <div class="text-xs text-amber-800">
                  {alert.count} rows &middot; {exactCurrencyFormatter(alert.amount)}
                </div>
              </div>
            </div>
            <Button variant="outline" class="h-7 text-xs" onClick={() => navigate(alert.href)}>
              View Deductions
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}
